import React from 'react';
import { 
  XMarkIcon,
  GiftIcon,
  CheckCircleIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

/**
 * BundleDetailModal - Shows detailed bundle information
 * Mobile-first modal with bundle items and pricing breakdown
 */
const BundleDetailModal = ({ 
  isOpen, 
  onClose, 
  bundle, 
  quantity = 1,
  onQuantityChange,
  onAddToCart, 
  disabled 
}) => {
  if (!isOpen || !bundle) return null;

  const regularPrice = parseFloat(bundle.regular_price || 0);
  const bundlePrice = parseFloat(bundle.bundle_price || 0);
  const savings = regularPrice - bundlePrice;
  const discountPercent = regularPrice > 0 ? ((savings / regularPrice) * 100).toFixed(0) : 0;

  const totalPrice = bundlePrice * quantity;
  const totalSavings = savings * quantity;

  // Get main image
  let imgSrc = 'https://via.placeholder.com/300?text=Bundle';
  if (bundle.images && Array.isArray(bundle.images) && bundle.images.length > 0) {
    imgSrc = bundle.images[0];
  } else if (bundle.bundle_image) {
    imgSrc = bundle.bundle_image;
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <GiftIcon className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">פרטי חבילה</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Bundle Image */}
          <div className="relative mb-6">
            <img
              src={imgSrc}
              alt={bundle.title}
              className="w-full h-64 object-cover rounded-xl"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/300?text=Bundle'; }}
            />
            {discountPercent > 0 && (
              <div className="absolute top-4 right-4 bg-green-500 text-white text-lg font-bold px-4 py-2 rounded-full shadow-lg">
                חיסכון {discountPercent}%
              </div>
            )}
          </div>

          {/* Title & Description */}
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {bundle.title}
          </h3>
          {bundle.description && (
            <p className="text-gray-600 mb-6">
              {bundle.description}
            </p>
          )}

          {/* SKU & Category */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-600">
              מק״ט: {bundle.bundle_sku}
            </span>
            {bundle.category && (
              <span className="text-xs bg-blue-100 px-3 py-1 rounded-full text-blue-600">
                {bundle.category.name}
              </span>
            )}
            {bundle.bundle_type && (
              <span className="text-xs bg-purple-100 px-3 py-1 rounded-full text-purple-600">
                {bundle.bundle_type === 'fixed' ? 'חבילה קבועה' : 'חבילה גמישה'}
              </span>
            )}
          </div>

          {/* Items in Bundle */}
          {bundle.bundle_items && bundle.bundle_items.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                פריטים בחבילה ({bundle.bundle_items.length})
              </h4>
              <div className="space-y-2">
                {bundle.bundle_items.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    {item.product?.images?.[0] && (
                      <img
                        src={item.product.images[0]}
                        alt={item.product.name}
                        className="w-12 h-12 object-cover rounded"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/50'; }}
                      />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {item.product?.name || 'מוצר'}
                      </div>
                      <div className="text-sm text-gray-600">
                        כמות: {item.quantity}
                      </div>
                    </div>
                    <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Breakdown */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">מחיר רגיל:</span>
                <span className="text-gray-400 line-through text-lg">
                  ₪{regularPrice.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-bold text-purple-900">מחיר חבילה:</span>
                <span className="text-2xl font-bold text-purple-600">
                  ₪{bundlePrice.toFixed(2)}
                </span>
              </div>
              {savings > 0 && (
                <div className="flex justify-between items-center pt-2 border-t border-purple-200">
                  <span className="font-semibold text-green-700">💰 חיסכון:</span>
                  <span className="text-xl font-bold text-green-600">
                    ₪{savings.toFixed(2)} ({discountPercent}%)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Validity Period */}
          {bundle.valid_until && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <div className="flex items-center gap-2 text-yellow-800">
                <span className="text-sm">🕒</span>
                <span className="text-sm font-medium">
                  המבצע תקף עד: {new Date(bundle.valid_until).toLocaleDateString('he-IL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          )}

          {/* Quantity Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              כמות:
            </label>
            <div className="flex items-center gap-4">
              <button
                onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className="p-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MinusIcon className="h-5 w-5" />
              </button>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => onQuantityChange(parseInt(e.target.value) || 1)}
                className="w-20 text-center border-2 border-purple-300 rounded-lg py-2 text-lg font-semibold focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={() => onQuantityChange(quantity + 1)}
                className="p-2 border-2 border-purple-600 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Total Price Summary */}
          {quantity > 1 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">סה״כ ({quantity} חבילות):</span>
                <span className="text-xl font-bold text-purple-600">
                  ₪{totalPrice.toFixed(2)}
                </span>
              </div>
              {totalSavings > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-green-700">סה״כ חיסכון:</span>
                  <span className="font-bold text-green-600">
                    ₪{totalSavings.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Add to Cart Button */}
          <button
            onClick={() => {
              onAddToCart(bundle, quantity);
              onClose();
            }}
            disabled={disabled}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 shadow-lg"
          >
            <PlusIcon className="h-6 w-6" />
            הוסף לעגלה בעבור ₪{totalPrice.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleDetailModal;

